
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import GameCard from '@/components/GameCard';
import { Lock } from 'lucide-react';
import { Game } from '@/data/games';
import { useToast } from '@/components/ui/use-toast';
import AuthDialog from '@/components/AuthDialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface GameSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  games: Game[];
  onGameSelect: (game: Game) => void;
  session: any;
  maxFreeGames: number;
  playedGamesCount: number;
}

const GameSelectionDialog = ({ 
  open, 
  onOpenChange, 
  games, 
  onGameSelect, 
  session, 
  maxFreeGames, 
  playedGamesCount 
}: GameSelectionDialogProps) => {
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const { toast } = useToast();

  const handleGameClick = (game: Game) => {
    // For authenticated users, navigate directly to the game
    if (session) {
      onGameSelect(game);
      return;
    }
    
    // For non-authenticated users, check if they've reached the limit
    if (game.id > maxFreeGames && playedGamesCount >= maxFreeGames) {
      onOpenChange(false); // Close the game selection dialog
      setIsAuthDialogOpen(true); // Open the auth dialog
      toast({
        title: "Game limit reached",
        description: "Create a free account to unlock all games and save your progress.",
      });
      return;
    }
    
    // Non-authenticated users can play limited games
    onGameSelect(game);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Choose Your Game</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            <div className="grid gap-4 py-4">
              {games.map((game) => {
                const isGameLocked = !session && game.id > maxFreeGames && playedGamesCount >= maxFreeGames;
                
                return (
                  <div key={game.id} className="relative">
                    <GameCard
                      title={game.title}
                      duration={game.duration}
                      description={isGameLocked ? "Available after creating an account" : game.description}
                      onClick={() => handleGameClick(game)}
                      className={isGameLocked ? "opacity-75" : ""}
                    />
                    {isGameLocked && (
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <Lock className="h-6 w-6 text-gray-500" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {/* Auth Dialog managed by GameSelectionDialog */}
      <AuthDialog
        open={isAuthDialogOpen}
        onOpenChange={setIsAuthDialogOpen}
      />
    </>
  );
};

export default GameSelectionDialog;
